import React from 'react';
import { Chip, Box, Typography } from '@mui/material';
import { useFilters } from '../context/FilterContext';
import { formatDateForDisplay } from '../utils/filterUtils';

/**
 * Component that displays active filters as chips with ability to remove them
 */
const FilterChips = ({ roleLabels = {}, providerLabels = {} }) => {
  const { 
    filters, 
    clearFilter,
    resetFilters,
    activeFilterCount 
  } = useFilters();

  // Function to clear all filters
  const handleClearAllFilters = () => {
    // Clear all filters by using the resetFilters function
    resetFilters();
  };

  // Don't render if no filters are active
  if (activeFilterCount === 0) {
    return null;
  }

  return (
    <Box 
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        my: 2,
        alignItems: 'center'
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
        Active Filters:
      </Typography>

      {/* Search filter chip */}
      {filters.search && (
        <Chip
          label={`Search: ${filters.search}`}
          size="small"
          onDelete={() => clearFilter('search')}
          color="primary"
          variant="outlined"
        />
      )}

      {/* Status filter chip */}
      {filters.isActive !== null && (
        <Chip
          label={`Status: ${filters.isActive ? 'Active' : 'Inactive'}`}
          size="small"
          onDelete={() => clearFilter('isActive')}
          color="primary"
          variant="outlined"
        />
      )}

      {/* Role filter chips */}
      {filters.roles && filters.roles.length > 0 && (
        <Chip
          label={`Roles: ${filters.roles.map(role => roleLabels[role] || role).join(', ')}`}
          size="small"
          onDelete={() => clearFilter('roles')}
          color="primary"
          variant="outlined"
        />
      )}

      {/* Provider filter chips */}
      {filters.providers && filters.providers.length > 0 && (
        <Chip
          label={`Providers: ${filters.providers.map(provider => providerLabels[provider] || provider).join(', ')}`}
          size="small"
          onDelete={() => clearFilter('providers')}
          color="primary"
          variant="outlined"
        />
      )}

      {/* Date range filter chip */}
      {(filters.dateRange?.start || filters.dateRange?.end) && (
        <Chip
          label={
            filters.dateRange.start && filters.dateRange.end
              ? `Created: ${formatDateForDisplay(filters.dateRange.start)} - ${formatDateForDisplay(filters.dateRange.end)}`
              : filters.dateRange.start
              ? `Created after: ${formatDateForDisplay(filters.dateRange.start)}`
              : `Created before: ${formatDateForDisplay(filters.dateRange.end)}`
          }
          size="small"
          onDelete={() => clearFilter('dateRange')}
          color="primary"
          variant="outlined"
        />
      )}

      {/* Clear all filters button */}
      {activeFilterCount > 1 && (
        <Chip
          label="Clear All Filters"
          size="small"
          onDelete={handleClearAllFilters}
          onClick={handleClearAllFilters}
          color="error"
        />
      )}
    </Box>
  );
};

export default FilterChips; 